class SchedulingUtils {
    /**
     * Checks if the given user ID matches the UTM value and if the date is within the specified range.
     *
     * @param {string} id - The user ID to check.
     * @param {string} utmValue - The UTM value to compare against the user ID.
     * @param {boolean} isDateBetween - A boolean indicating if the current date is within the specified range.
     * @returns {boolean} - Returns true if the user ID matches the UTM value and the date is within the range, otherwise false.
     */
    static isUserUtm(id, utmValue, isDateBetween) {
        if (!id) return false;
        if (!isDateBetween) return false;
        return id === utmValue;
    }

    /**
     * Checks if any of the given UTM Elements are required.
     *
     * This function iterates over an array-like object of UTM elements and checks
     * if any of them have a `data-utm-required` attribute set to 'true'.
     *
     * @param {NodeList|HTMLCollection} elements - A collection of elements.
     * @returns {boolean} - Returns `true` if at least one element required UTM and the item is not schedule or expired otherwise `false`.
     */
    static checkIfRequiredUtmElement(elements) {
        //todo maybe : at least exist one content utm but with no future or past date scheduling
        return Array.from(elements)
            .some(item => item.dataset.utmRequired == 'true' && DateUtils.checkDateBetween(item.dataset.timezone, item.dataset.dateFrom, item.dataset.dateTo));
    }

    /**
     * Checks if any of the given elements require the user to be logged in.
     *
     * @param {NodeList|HTMLCollection} elements - The collection of elements to check.
     * @returns {boolean} - Returns true if at least one element has a data attribute 'loggedRequired' set to 'true' and the item is not schedule or expired, otherwise false.
     */
    static checkIfRequiredLogged(elements) {
        //todo maybe : at least exist one content utm but with no future or past date scheduling
        return Array.from(elements)
            .some(item => item.dataset.loggedRequired == 'true' && DateUtils.checkDateBetween(item.dataset.timezone, item.dataset.dateFrom, item.dataset.dateTo));
    }

    /**
     * Determines whether an item should be removed based on various conditions.
     *
     * @param {boolean} isUserLogged - Indicates if the user is logged in.
     * @param {boolean} loggedInRequired - Indicates if being logged in is required.
     * @param {boolean} notLoggedInRequired - Indicates if being not logged in is required.
     * @param {boolean} isOneLoggedRequiredPresent - Indicates if at least one logged-in requirement is present.
     * @param {string} userUtm - The UTM parameter associated with the user.
     * @param {string} requiredUtm - The required UTM parameter.
     * @param {boolean} isOneUtmRequiredPresent - Indicates if at least one UTM requirement is present.
     * @param {boolean} isDateBetweenScheduleContent - Indicates if the current date is within the scheduled content date range.
     * @returns {boolean} - Returns true if the item should be removed, otherwise false.
     */
    static checkIfRemoveItem(isUserLogged, loggedInRequired, notLoggedInRequired, isOneLoggedRequiredPresent, userUtm, requiredUtm, utmNotRequired, isOneUtmRequiredPresent, isDateBetweenScheduleContent){
        //hide element if date is not between schedule content
        if (!isDateBetweenScheduleContent) return true;

        let remove = false;

        if(!notLoggedInRequired &&  !loggedInRequired &&  !requiredUtm && !utmNotRequired){
            remove = false;
        }
        else if(requiredUtm && userUtm){
            remove = false;
        }
        else if(loggedInRequired && isUserLogged){
            remove = false;
        }
        else if(notLoggedInRequired && !isUserLogged){
            remove = false;
        }
        else if(utmNotRequired && !userUtm){
            remove = false;
        }
        else{
            remove = true;
        }
        return remove;
    }
}

class ContentScheduling extends HTMLElement{
    constructor(){
        super();
        this.requiredUtm      = this.dataset.utmRequired == 'true';
        this.scheduleDateFrom = this.dataset.dateFrom;
        this.scheduleDateTo   = this.dataset.dateTo;
        this.scheduleTimezone = this.dataset.timezone;
        this.loggedInRequired = this.dataset.loggedRequired == 'true';
        this.notLoggedInRequired = this.dataset.notLoggedRequired == 'true';
        this.utmRequired      = this.dataset.utmRequired == 'true';
        this.utmNotRequired   = this.dataset.utmNotRequired == 'true';

        this.utmTimeZone      = this.dataset.utmTimezone;
        this.utmDateFrom      = this.dataset.utmDateFrom;
        this.utmDateTo        = this.dataset.utmDateTo;
        this.utmId            = this.dataset.utmId;
        this.typeDisplay      = this.dataset.typeDisplay || 'block';

        this.removeWrapper    = this.dataset.removeWrapper == 'true';

        this.handleElement();
    }

    handleElement() {
        let isDateBetweenUTM             = DateUtils.checkDateBetween(this.utmTimeZone,this.utmDateFrom, this.utmDateTo);
        let isDateBetweenScheduleContent = DateUtils.checkDateBetween(this.scheduleTimezone, this.scheduleDateFrom, this.scheduleDateTo);
        let isUserLogged                 = this.dataset.customer != '';
        let isOneUtmRequiredPresent      = SchedulingUtils.checkIfRequiredUtmElement(this.querySelectorAll(this.tagName.toLowerCase()));
        let isOneLoggedRequiredPresent   = SchedulingUtils.checkIfRequiredLogged(this.querySelectorAll(this.tagName.toLowerCase()));
        let isUserUtm                    = SchedulingUtils.isUserUtm(this.utmId, UrlUtils.getQueryParam('cid') || CookieUtils.getCookie('cid'), isDateBetweenUTM);
        let removeElement                = SchedulingUtils.checkIfRemoveItem(isUserLogged, this.loggedInRequired, this.notLoggedInRequired, isOneLoggedRequiredPresent, isUserUtm, this.requiredUtm, this.utmNotRequired, isOneUtmRequiredPresent, isDateBetweenScheduleContent);

        if(removeElement){
            this.remove();
            return;
        }
        this.style.display = this.typeDisplay;
        if(this.removeWrapper){
            this.replaceWith(...this.childNodes);
        }
    }
}

customElements.define('content-scheduling', ContentScheduling);